"use client";

import clsx from "clsx";
import Tooltip from "@/components/library/Tooltip";
import { useEffect, useMemo, useRef, useState, type JSX } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Mention, MentionsInput } from "react-mentions";
import { GenericInputTypes } from "@/types/common/library";
import { DedupeVariablesByCase } from "@/utils/agencies/variables";
import Icon from "./Icon";

const suggestionsContainerStyle = {
  maxHeight: "150px",
  overflowY: "auto",
  padding: "0.25rem",
  border: "1px solid #e0e0e0",
} as React.CSSProperties;
// Regex to find all properly formatted variables to check for unknown IDs (used for validation)
// Using non-greedy capture to match everything between the opening and closing brackets/parentheses
const ALL_VARIABLES_REGEX = /@\[([^\]]+)\]\(((?:[^)(]|\([^)(]*\))+)\)/g;
// Non-global version for the Mention component (used for styling)
const ALL_VARIABLES_REGEX_NON_GLOBAL =
  /@\[([^\]]+)\]\(((?:[^)(]|\([^)(]*\))+)\)/;

type VariableEditorProps = Omit<GenericInputTypes, "label"> & {
  label?: string | JSX.Element;
  tooltip?: string;
  setValue: (newValue: string) => void;
  variables: string[];
  compact?: boolean;
  autoFocus?: boolean;
  enterAction?: () => void;
  parentElement?: Element;
};

// Regular expression for highlighting valid variables (correct format AND ID in variables array)
const createValidVariablesRegex = (validVariables: string[]): RegExp => {
  if (!validVariables || validVariables.length === 0)
    return /(?!)([a-z]+)(?!)([a-z]+)/; // Never matches anything but has two capture groups

  // Escape any special characters in variable names, especially parentheses
  const escapedVariables = validVariables.map((v) =>
    v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  );
  const variableIdsPattern = escapedVariables.join("|");

  // This regex will match variables with display part in brackets and ID in variables array
  // Using exactly two capture groups: one for display text and one for the ID
  // Pattern handles nested parentheses in IDs (like "Buyer (True) Test")
  return new RegExp(`@\\[([^\\]]+)\\]\\((${variableIdsPattern})\\)`);
};

const VariableEditor: React.FC<VariableEditorProps> = ({
  id,
  label,
  tooltip,
  placeholder,
  className,
  value,
  setValue,
  variables,
  helperText,
  error,
  ["data-testid"]: dataTestId,
  required,
  compact,
  autoFocus,
  disabled,
  enterAction,
  parentElement,
}) => {
  const { t: ct } = useTranslation("common");

  // Internal state to preserve cursor position during user edits.
  // The external `value` prop is often recomputed through transformations on
  // every render (e.g. UpdateDynamicContentUI/GetDynamicContentUI), which causes
  // react-mentions to lose cursor position. By buffering through internal state
  // and only syncing from external when the textarea is not focused, we keep the
  // cursor stable while the user is typing.
  const [internalValue, setInternalValue] = useState(
    (value as string) ?? ""
  );
  const isFocused = useRef(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!isFocused.current) {
      setInternalValue((value as string) ?? "");
    }
  }, [value]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Same case-insensitive collapse as Try Agent (lexicographically smallest
  // spelling wins — not API order) so @-picking a token matches the test-call
  // form (GAIL-1110). Full `variables` is still used for validation so an
  // already-saved alternate spelling stays green.
  const suggestionVariables = useMemo(
    () => DedupeVariablesByCase(variables || []),
    [variables]
  );

  const [invalidVariables, setInvalidVariables] = useState<
    Array<{ id: string; display: string }>
  >([]);

  useEffect(() => {
    if (!value) return;

    // Find invalid variables in the content
    const invalid: Array<{ id: string; display: string }> = [];
    const text = value as string;

    // Only find unknown variables (correctly formatted but ID not in variables array)
    ALL_VARIABLES_REGEX.lastIndex = 0;
    let match;
    while ((match = ALL_VARIABLES_REGEX.exec(text)) !== null) {
      if (match && match.length >= 3) {
        const variableId = match[2].trim(); // ID part (in parentheses), trimmed

        // Check if this ID exists in the variables array (trimmed comparison)
        const trimmedVariables = variables.map((v) => v.trim());
        const isValid = trimmedVariables.includes(variableId);

        if (!isValid) {
          // Only add if it's not already in the invalid list
          const fullMatch = match[0];
          if (!invalid.some((item) => item.id === fullMatch)) {
            invalid.push({
              id: fullMatch,
              display: fullMatch,
            });
          }
        }
      }
    }

    setInvalidVariables(invalid);
  }, [value, variables]);

  // Create a regex that only matches valid variables (with IDs in the variables array)
  const validVariablesRegex = createValidVariablesRegex(variables);

  const showRequiredLabel = !!label && !!required;

  return (
    <div
      className={className}
      data-testid={dataTestId}
      // Prevent blur from bubbling to dialog focus trap when clicking another input
      onBlurCapture={(e) => {
        const dialogContent = e.currentTarget.closest("[data-dialog-content]");
        if (
          dialogContent &&
          e.relatedTarget &&
          dialogContent.contains(e.relatedTarget as Node)
        ) {
          e.stopPropagation();
        }
      }}
    >
      {label && (
        <span className="text-sm">
          <Tooltip
            content={
              <div>
                {tooltip}
                {tooltip && <br />}
                {ct("variableEditorTooltip1")}
                <br />
                {ct("variableEditorTooltip2")}
                <br />
              </div>
            }
            className="z-50"
          >
            <div className={clsx("mb-1", "font-medium")}>
              {showRequiredLabel ? <>{label} *</> : <>{label}</>}
              <Icon icon="circle-question" className="ml-2" />
            </div>
          </Tooltip>
        </span>
      )}
      <MentionsInput
        id={id}
        disabled={disabled}
        value={internalValue}
        placeholder={placeholder}
        inputRef={inputRef}
        customSuggestionsContainer={(children) => (
          <div style={suggestionsContainerStyle}>{children}</div>
        )}
        suggestionsPortalHost={parentElement ?? undefined}
        onFocus={() => {
          isFocused.current = true;
        }}
        onBlur={() => {
          // Defer so the current render cycle (from the last onChange) completes
          // before we start accepting external value syncs again.
          setTimeout(() => {
            isFocused.current = false;
          }, 0);
        }}
        onChange={(e) => {
          setInternalValue(e.target.value);
          setValue(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey && compact && enterAction) {
            e.preventDefault();
            enterAction();
          }
        }}
        style={{
          suggestions: {
            zIndex: 9999,
          },
        }}
        className={clsx(
          "py-2",
          "px-4",
          "rounded-xl",
          "[&_textarea]:rounded-xl",
          "[&_textarea]:min-h-24",
          "[&>div>div]:min-h-24",
          "[&_textarea]:ring-gray-300",
          "[&_textarea]:dark:ring-gray-600",
          "[&_textarea]:border-gray-300",
          "[&_textarea]:dark:border-gray-600",
          "[&>div>div]:!left-[-4px]",
          "[&>div>div]:!w-[calc(100%+8px)]",
          "[&_div:nth-child(2)]:dark:!bg-black",
          "[&_div:nth-child(2)]:border",
          "[&_div:nth-child(2)]:rounded",
          "[&_div:nth-child(2)]:!mt-[30px]",
          "[&_div:nth-child(2)]:!p-2",
          "[&_div:nth-child(2)]:border-gray-300",
          "[&_div:nth-child(2)]:dark:border-gray-600",
          "[&_.py-2__suggestions__item--focused]:!bg-red-500",
          {
            "[&_textarea]:min-h-4 [&>div>div]:min-h-4": compact,
            "[&_textarea]:min-h-20 [&>div>div]:min-h-20": !compact,
            "[&_textarea]:cursor-not-allowed !bg-gray-200 dark:!bg-gray-600":
              disabled,
            "!bg-white dark:!bg-gray_comp": !disabled,
          }
        )}
      >
        {/* Regular variables (correct format AND ID is in variables array) */}
        <Mention
          trigger={suggestionVariables.length > 0 ? "@" : ""}
          data={suggestionVariables.map((variable) => ({
            id: variable,
            display: ct(variable),
          }))}
          style={{
            background: "rgba(var(--gail-color-primary) / 0.6)",
          }}
          markup="@[__display__](__id__)"
          regex={validVariablesRegex}
          displayTransform={(id, display) => display}
        />

        {/* Unknown variables - correctly formatted but ID not in variables array */}
        <Mention
          trigger=""
          data={[]}
          style={{
            background: "rgba(224, 36, 36, 0.6)",
          }}
          regex={ALL_VARIABLES_REGEX_NON_GLOBAL}
          markup="@[__display__](__id__)"
          displayTransform={(id, display) => display}
        />
      </MentionsInput>
      {helperText && (
        <p
          className={clsx("text-xs", "mt-1", "text-gray-500", {
            "text-red-600": error,
          })}
        >
          {helperText}
        </p>
      )}
      {invalidVariables.length > 0 && (
        <p className="mt-1 text-red-600 text-xs">
          {ct("invalidVariableFormat")}
        </p>
      )}
    </div>
  );
};

export default VariableEditor;
